/**
 * Copyright 2021 Olympe S.A.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { registerBrick, GlobalProperties } from 'olympe';
import { ReactBrick, useProperty, cssToSxProps, ifNotTransparent, ifNotNull, getLogger } from '@olympeio/core';

import React from 'react';
import Box from '@mui/material/Box';
import Zoomist from 'zoomist'
import { combineLatestWith } from "rxjs/operators";
import {Typography} from '@mui/material';

/**
 * Provide a Image visual component using MUI CardMedia
 */
export default class ZoomablePanel extends ReactBrick {

    /**
     * @override
     */
    setupExecution($) {
        $.set('zoomist-brick-id', 'zoomist-'+this.getTag());
        return $.observe('Renderer', false).pipe(combineLatestWith($.observe('Hidden')));
    }

    /**
     * @override
     */
    updateParent(parent, element) {
        parent.style.overflow = 'visible'; // Allow overflow
        return super.updateParent(parent, element);
    }


    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [renderer, hidden] = props.values;
            const [borderColor, borderRadius, borderWidth, cssProperty, defaultColor] = [
                'Border Color', 'Border Radius', 'Border Width', 'CSS Property', 'Default Color'
            ].map((p) => useProperty($, p));

            const draggable = useProperty($, 'draggable');
            const wheelable = useProperty($, 'wheelable');
            const pinchable = useProperty($, 'pinchable');
            const bounds = useProperty($, 'bounds');
            const zoomRatio = useProperty($, 'zoom ratio');
            const maxScale = useProperty($, 'max scale');
            const minScale = useProperty($, 'min scale');
            const initScale = useProperty($, 'init scale');
            const slider = useProperty($, 'slider');
            const zoomer = useProperty($, 'zoomer');
            const options = {
                draggable,
                wheelable,
                pinchable,
                bounds,
                zoomRatio,
                maxScale,
                minScale,
                initScale,
                slider: slider !== 'none' ? {direction: slider} : false,
                zoomer
            }

            // No renderer -> show a placeholder in DRAW
            if (!hidden && !renderer && $.get(GlobalProperties.EDITION, true)) {
                return <Box sx={{backgroundColor: 'lightgrey', width: 1, height: 1, overflow: 'hidden'}}>
                    <Typography sx={{color: 'black', padding: 1}}>
                        <b>Zoomable panel</b><br/>
                        Specify <code>Renderer</code> property to use the component.
                    </Typography>
                </Box>;
            }

            const [boxEl, setBoxEl] = React.useState(null);

            // Create and destroy runner using useEffect
            React.useEffect(() => {
                let renderer$;
                if (boxEl && !hidden && renderer) {
                    renderer$ = $.runner(renderer).setParentElement(boxEl);
                    const props = [
                        'Allow Content Overflow',
                        'Height',
                        'Tab Index',
                        'Width',
                        'Z-Index'
                    ];
                    props.forEach(prop => {
                        renderer$.repeat(prop, $.observe(prop));
                    });
                    renderer$.observe('On Click').subscribe($.trigger.bind('On Click'));

                }
                return () => {
                    renderer$?.destroy();
                };
            }, [boxEl]);


            React.useEffect(() => {
                if (!boxEl) {
                    return;
                }
                setTimeout(() => {
                    const zoomistObj = $.get('zoomist-object');
                    if (zoomistObj) {
                        zoomistObj.update(options);
                    } else {
                        try {
                            const zoomist = new Zoomist('#'+$.get('zoomist-brick-id'), options);
                            $.set('zoomist-object', zoomist)
                        } catch (e) {
                            getLogger('ZoomablePanel').error(e);
                        }
                    }
                }, 500);
            }, [boxEl, draggable, wheelable, pinchable, bounds, zoomRatio, maxScale, minScale, initScale, slider, zoomer]);

            // Execute runner
            return !hidden && (
                <Box id={$.get('zoomist-brick-id')}
                     sx={{
                        width: 1,
                        height: 1,
                        boxSizing: 'border-box',
                        position: 'absolute',
                        overflow: useProperty($, 'Allow Content Overflow') ? 'visible' : 'hidden',
                        ...ifNotNull('borderWidth', useProperty($, 'Border Width')),
                        ...ifNotTransparent('backgroundColor', defaultColor),
                        ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                        ...ifNotTransparent('borderColor', borderColor),
                        ...ifNotTransparent('borderStyle', 'solid', borderColor),
                        ...ifNotNull('boxSizing', 'border-box', borderWidth),
                        ...cssToSxProps(useProperty($, cssProperty))

                     }}
                     onClick={() => $.trigger('On Click')}
                >
                    <Box className="zoomist-wrapper" sx={{ width: 1, height: 1}}>
                        <Box className="zoomist-image" sx={{ width: 1, height: 1}}>
                            <Box ref={setBoxEl} />
                        </Box>
                    </Box>
                </Box>);
        };
    }
}

registerBrick('018dd662e53d7b172b02', ZoomablePanel);
